import AsyncStorage from '@react-native-async-storage/async-storage';

const GITHUB_API_BASE_URL = 'https://api.github.com/repos/The-Nust-Life/nust-timetables';
const GITHUB_RAW_BASE_URL = 'https://raw.githubusercontent.com/The-Nust-Life/nust-timetables/main';
const TIMETABLE_CACHE_PREFIX = '@timetable_cache:';
const FACULTIES_CACHE_KEY = '@faculties_cache';
const CACHE_EXPIRY_HOURS = 24; // Cache timetables for 24 hours

export interface TimetableInfo {
  id: string;
  name: string;
  faculty: string;
  filename: string;
}

export class GitHubTimetableService {
  static async getFaculties(): Promise<TimetableInfo[]> {
    try {
      // Check cache first
      const cachedData = await AsyncStorage.getItem(FACULTIES_CACHE_KEY);
      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        const age = Date.now() - timestamp;
        if (age < CACHE_EXPIRY_HOURS * 60 * 60 * 1000) {
          return data;
        }
      }

      // Clear any existing cache
      await this.clearCache();

      // Fetch the list of faculty folders from the main branch
      const foldersResponse = await fetch(`${GITHUB_API_BASE_URL}/contents?ref=main`);
      if (!foldersResponse.ok) {
        throw new Error('Failed to fetch faculty folders');
      }
      const folders = await foldersResponse.json();
      
      const timetables: TimetableInfo[] = [];

      // For each faculty folder
      for (const folder of folders) {
        if (folder.type === 'dir') {
          // Fetch timetables in this faculty
          const filesResponse = await fetch(`${GITHUB_API_BASE_URL}/contents/${encodeURIComponent(folder.path)}?ref=main`);
          if (!filesResponse.ok) {
            console.error(`Failed to fetch files for faculty: ${folder.name}`);
            continue;
          }
          const files = await filesResponse.json();
          
          // Add each ICS file as a timetable
          for (const file of files) {
            if (file.name.endsWith('.ics')) {
              const name = file.name.replace('.ics', '').replace(/-/g, ' ');
              timetables.push({
                id: file.name.replace('.ics', '').toLowerCase(),
                name: name,
                faculty: decodeURIComponent(folder.name),
                filename: file.name
              });
            }
          }
        }
      }

      // Cache the results
      await AsyncStorage.setItem(FACULTIES_CACHE_KEY, JSON.stringify({
        data: timetables,
        timestamp: Date.now()
      }));

      return timetables;
    } catch (error) {
      console.error('Error fetching faculties:', error);
      throw error;
    }
  }

  static getTimetableUrl(timetable: TimetableInfo): string {
    const path = `${encodeURIComponent(timetable.faculty)}/${encodeURIComponent(timetable.filename)}`;
    return `${GITHUB_RAW_BASE_URL}/${path}`;
  }

  static async fetchTimetable(timetableId: string): Promise<string> {
    try {
      // Check cache first
      const cachedData = await this.getCachedTimetable(timetableId);
      if (cachedData) {
        return cachedData;
      }

      // Get all timetables
      const timetables = await this.getFaculties();
      const timetable = timetables.find(t => t.id === timetableId);
      
      if (!timetable) {
        throw new Error('Timetable not found');
      }

      // Fetch the timetable content
      const response = await fetch(this.getTimetableUrl(timetable));
      if (!response.ok) {
        throw new Error('Failed to fetch timetable content');
      }
      const content = await response.text();

      // Cache the result
      await this.cacheTimetable(timetableId, content);

      return content;
    } catch (error) {
      console.error('Error fetching timetable:', error);
      throw error;
    }
  }

  private static async getCachedTimetable(timetableId: string): Promise<string | null> {
    try {
      const cacheKey = `${TIMETABLE_CACHE_PREFIX}${timetableId}`;
      const cachedData = await AsyncStorage.getItem(cacheKey);
      
      if (cachedData) {
        const { content, timestamp } = JSON.parse(cachedData);
        const age = Date.now() - timestamp;
        
        if (age < CACHE_EXPIRY_HOURS * 60 * 60 * 1000) {
          return content;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return null;
    }
  }

  private static async cacheTimetable(timetableId: string, content: string): Promise<void> {
    try {
      const cacheKey = `${TIMETABLE_CACHE_PREFIX}${timetableId}`;
      await AsyncStorage.setItem(cacheKey, JSON.stringify({
        content,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error writing to cache:', error);
    }
  }

  static async clearCache(): Promise<void> {
    try {
      const keys = await AsyncStorage.getAllKeys();
      const timetableKeys = keys.filter(key => 
        key.startsWith(TIMETABLE_CACHE_PREFIX) || key === FACULTIES_CACHE_KEY
      );
      await AsyncStorage.multiRemove(timetableKeys);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }
}
